import type { StyleSpecification as MapStyle } from 'mapbox-gl';
import { useEffect, useState } from 'react';
import { MAP_STYLES } from 'src/lib/player/constants';
import type { ExternalLayerType } from 'src/lib/player/store';

// From https://github.com/mapbox/mapbox-gl-language/issues/31#issuecomment-510307581
function fixLanguage(layers: Array<any>, language: string) {
  const labelList = layers.filter((layer) => {
    return /-label/.test(layer.id);
  });
  for (const labelLayer of labelList) {
    labelLayer.layout['text-field'] = ['coalesce', ['get', `name_${language}`], ['get', 'name']];
  }
}

function getMapStyleKey(mapType: string) {
  const mapStyleInfo = MAP_STYLES.find((info) => info.label === mapType);
  return mapStyleInfo != null ? mapStyleInfo.key : MAP_STYLES[0].key;
}

const mapStyleCache = new Map<string, Promise<MapStyle>>();

async function loadMapStyle(mapStyleKey: string): Promise<MapStyle> {
  const res = await fetch(
    `https://api.mapbox.com/styles/v1/mapbox/${mapStyleKey}?optimize=true&access_token=${import.meta.env.VITE_FRONTEND_MAPBOX_ACCESS_TOKEN}`,
  );
  const mapStyleData = await res.json();
  fixLanguage(mapStyleData.layers, 'mul');
  return mapStyleData;
}

function loadMapStyleMemoized(mapStyleKey: string): Promise<MapStyle> {
  let promise = mapStyleCache.get(mapStyleKey);
  if (promise == null) {
    promise = loadMapStyle(mapStyleKey);
    mapStyleCache.set(mapStyleKey, promise);
    promise.catch(() => mapStyleCache.delete(mapStyleKey));
  }
  return promise;
}

// Starts the style and the mapbox-gl chunk while the event data is still loading
export function prefetchMap(mapType: string): void {
  loadMapStyleMemoized(getMapStyleKey(mapType)).catch(() => {});
  import('mapbox-gl').catch(() => {});
}

function drawExternalLayers(
  mapStyle: MapStyle,
  externalLayers: Array<ExternalLayerType>,
): MapStyle {
  return {
    ...mapStyle,
    layers: [
      ...mapStyle.layers,
      ...externalLayers.map(({ id }) => ({ id, type: 'raster' as const, source: id, paint: {} })),
    ],
    sources: {
      ...mapStyle.sources,
      ...Object.fromEntries(
        externalLayers.map(({ id, tiles, tileSize }) => [
          id,
          { type: 'raster' as const, tiles, tileSize },
        ]),
      ),
    },
  };
}
export function useMapStyle(mapType: string, externalLayers: Array<ExternalLayerType>) {
  const [mapStyle, setMapStyle] = useState<MapStyle | null>(null);

  useEffect(() => {
    (async () => {
      let mapStyleData = await loadMapStyleMemoized(getMapStyleKey(mapType));
      mapStyleData = drawExternalLayers(mapStyleData, externalLayers);
      setMapStyle(mapStyleData);
    })();
  }, [mapType, externalLayers]);

  return mapStyle;
}
